import { createContext, useContext, useState, useEffect, useRef } from 'react'
import { getFirebaseAuth } from '../firebase'

const AuthContext = createContext()

const SESSION_TIMEOUT_MS = 60 * 60 * 1000 // 1 hour
const HAS_LOGGED_IN_KEY = 'hasAdminSession'

// Only worth checking Firebase's actual auth state (firebase/auth chunk +
// its auth-domain iframe + a relyingparty config fetch - ~230KB and several
// round trips per PageSpeed's own network trace) if this browser has either
// logged in as admin before, or is on an admin route right now. Every other
// visit is a customer who will never be `currentUser`, so there's nothing to
// check - skip the fetch and resolve to "logged out" immediately instead of
// making every storefront visitor pay for it.
const shouldCheckAuth = () => {
  if (localStorage.getItem(HAS_LOGGED_IN_KEY)) return true
  const path = window.location.pathname
  return path.startsWith('/admin') || path === '/in-shop-sales'
}

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null)
  const [authLoading, setAuthLoading] = useState(shouldCheckAuth)
  const logoutTimerRef = useRef(null)
  // Populated once the lazy-loaded firebase/auth chunk resolves - see
  // getFirebaseAuth() in firebase.js for why this isn't a static import.
  const authModuleRef = useRef(null)

  const resetLogoutTimer = () => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current)
    }
    if (currentUser) {
      logoutTimerRef.current = setTimeout(() => {
        getFirebaseAuth().then((mod) => mod.signOut(mod.auth))
      }, SESSION_TIMEOUT_MS)
    }
  }

  useEffect(() => {
    if (currentUser) {
      localStorage.setItem('lastActivity', Date.now().toString())
      resetLogoutTimer()
      const handleActivity = () => {
        localStorage.setItem('lastActivity', Date.now().toString())
        resetLogoutTimer()
      }
      window.addEventListener('mousemove', handleActivity)
      window.addEventListener('keypress', handleActivity)
      window.addEventListener('scroll', handleActivity)
      window.addEventListener('click', handleActivity)
      return () => {
        window.removeEventListener('mousemove', handleActivity)
        window.removeEventListener('keypress', handleActivity)
        window.removeEventListener('scroll', handleActivity)
        window.removeEventListener('click', handleActivity)
        if (logoutTimerRef.current) {
          clearTimeout(logoutTimerRef.current)
        }
      }
    } else {
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current)
      }
    }
  }, [currentUser])

  useEffect(() => {
    if (!shouldCheckAuth()) return

    let unsubscribe = () => {}
    let cancelled = false

    const loadAuth = () => {
      getFirebaseAuth().then((mod) => {
        if (cancelled) return
        authModuleRef.current = mod
        // Firebase tells us whenever login state changes (login, logout, page refresh)
        unsubscribe = mod.onAuthStateChanged(mod.auth, (user) => {
          setCurrentUser(user)
          if (user) {
            localStorage.setItem('lastActivity', Date.now().toString())
            resetLogoutTimer()
          } else {
            localStorage.removeItem('lastActivity')
          }
          setAuthLoading(false)
        })
      })
    }

    // Scheduled for idle time rather than firing immediately, so it never
    // competes with the critical rendering path. Safari has no
    // requestIdleCallback, hence the setTimeout fallback.
    let idleHandle
    if ('requestIdleCallback' in window) {
      idleHandle = window.requestIdleCallback(loadAuth, { timeout: 2000 })
    } else {
      idleHandle = setTimeout(loadAuth, 1)
    }

    return () => {
      cancelled = true
      unsubscribe()
      if ('cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleHandle)
      } else {
        clearTimeout(idleHandle)
      }
    }
  }, [])

  // Check on initial load if session is expired
  useEffect(() => {
    if (!authLoading) {
      const lastActivity = localStorage.getItem('lastActivity')
      if (lastActivity && Date.now() - parseInt(lastActivity) > SESSION_TIMEOUT_MS) {
        getFirebaseAuth().then((mod) => mod.signOut(mod.auth))
      }
    }
  }, [authLoading])

  const login = async (email, password) => {
    const mod = await getFirebaseAuth()
    const result = await mod.signInWithEmailAndPassword(mod.auth, email, password)
    localStorage.setItem(HAS_LOGGED_IN_KEY, '1')
    return result
  }

  const logout = async () => {
    const mod = await getFirebaseAuth()
    return mod.signOut(mod.auth)
  }

  return (
    <AuthContext.Provider value={{ currentUser, authLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
